import debugx from "debugx";
import { Position } from "../types";
import { BaseTrackingProvider, TrackingData } from "./trackingProvider";
import { CompassHeading } from "../utils/compassHeading";
import { settingsService } from "./settingsService";

const debug = debugx("bikedash:device-tracking");

export class DeviceTrackingProvider extends BaseTrackingProvider {
  readonly id = "device";
  readonly name = "Device GPS";
  readonly type = "device" as const;

  private watchPositionId: number | null = null;
  private compassHeading: CompassHeading | null = null;
  private compassIntervalId: number | null = null;
  private currentCompassHeading: number | null = null;
  private lastCompassUpdateTime: number = 0;
  private lastPositionNotifyTime: number = 0;
  private currentUpdateRate: number = 30;
  private hasReceivedPosition: boolean = false;
  private errorDebounceTimer: NodeJS.Timeout | null = null;

  // GPS heading smoothing (uses GPS coords.heading from device)
  private targetGpsHeading: number | null = null;
  private smoothedGpsHeading: number | null = null;
  private gpsHeadingIntervalId: number | null = null;
  private lastGpsHeadingUpdate: number = 0;

  // Compass calibration using GPS as ground truth
  private compassCalibrationReadings: Array<{
    gps: number;
    compass: number;
    timestamp: number;
  }> = [];
  private compassOffset: number = 0;

  private getUpdateInterval(): number {
    const maxUpdateRate =
      settingsService.getSettings().user.maxUpdateRate || 30;
    return 1000 / maxUpdateRate; // Convert FPS to milliseconds
  }

  constructor() {
    super();
    debug("Device tracking provider initialized");
  }

  async isAvailable(): Promise<boolean> {
    return "geolocation" in navigator && navigator.geolocation !== null;
  }

  async start(): Promise<void> {
    if (this._isActive) {
      debug.warn("Device tracking already active");
      return;
    }

    if (!(await this.isAvailable())) {
      throw new Error("Geolocation is not available");
    }

    const mapPrefs = settingsService.getMapPreferences();

    // Start compass OR GPS heading (not both)
    if (mapPrefs.useGpsHeading) {
      debug("Using GPS heading mode (no compass permission needed)");
      this.startGpsHeadingSmoothing();
    } else {
      debug("Using compass heading mode");
      this.startCompass();
    }

    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 1000,
    };

    return new Promise((resolve, reject) => {
      const handlePosition = (pos: GeolocationPosition) => {
        this.hasReceivedPosition = true;

        // Clear any pending error notification - we got a valid position
        if (this.errorDebounceTimer) {
          clearTimeout(this.errorDebounceTimer);
          this.errorDebounceTimer = null;
        }

        const mapPrefs = settingsService.getMapPreferences();
        const gpsHeading = pos.coords.heading;
        const speed = pos.coords.speed ?? 0;
        const speedMph = speed * 2.23694; // Convert m/s to mph

        let heading: number | undefined;
        let headingUnreliable = false;
        let compassHeading: number | undefined;

        if (mapPrefs.useGpsHeading) {
          // GPS heading mode - use existing smoothing system for both GPS and compass
          // Start smoothing interval if not already started
          if (!this.gpsHeadingIntervalId) {
            this.startGpsHeadingSmoothing();
          }

          if (gpsHeading !== null && speedMph > 2) {
            // Moving fast enough - use GPS heading as target

            // Keep compass running for calibration if we have compass data
            if (!this.compassHeading) {
              this.startCompass();
            }

            // Calibrate compass using GPS as ground truth
            if (this.currentCompassHeading !== null) {
              this.updateCompassCalibration(
                gpsHeading,
                this.currentCompassHeading,
              );
            }

            // Update target to GPS, but DON'T set heading here
            // The smoothing interval will interpolate toward this target
            this.targetGpsHeading = gpsHeading;

            // Don't include heading in this position update
            // Let the smoothing interval handle all heading updates
            heading = undefined;
            compassHeading = undefined;
            headingUnreliable = true;

            debug(
              "🧭 GPS MODE: target=%s°, speed=%s mph, compass offset=%s° (heading from interval)",
              gpsHeading.toFixed(1),
              speedMph.toFixed(1),
              this.compassOffset.toFixed(1),
            );
          } else {
            // Too slow or no GPS heading - use compass heading as target
            // Start compass if not already running
            if (!this.compassHeading) {
              debug(
                "Starting compass (GPS unreliable, speed=%s mph)",
                speedMph.toFixed(1),
              );
              this.startCompass();
            }

            // Target will be updated in the smoothing interval directly from compass
            // Just set to null to signal we're in compass mode
            this.targetGpsHeading = null;

            // Don't include heading in this position update
            // Let the smoothing interval handle all heading updates
            heading = undefined;
            compassHeading = undefined;
            headingUnreliable = true;

            debug(
              "🧭 COMPASS MODE: target=%s°, speed=%s mph (heading from interval)",
              this.currentCompassHeading?.toFixed(1) ?? "none",
              speedMph.toFixed(1),
            );
          }
        } else {
          // Compass mode - ensure compass is running
          this.stopGpsHeadingSmoothing();

          // Start compass if not already running
          if (!this.compassHeading) {
            debug("Starting compass (switched from GPS heading mode)");
            this.startCompass();
          }

          compassHeading = this.currentCompassHeading ?? undefined;
          heading = compassHeading;
          headingUnreliable = heading === undefined;
        }

        const position: Position = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          heading,
          headingUnreliable,
          compassHeading,
          speed: pos.coords.speed ?? undefined,
          altitude: pos.coords.altitude ?? undefined,
          timestamp: pos.timestamp,
          isComplete: true,
          isInterpolated: false, // This is a real GPS position, not interpolated
        };

        const trackingData: TrackingData = {
          position,
          isActive: true,
          source: "device",
          metadata: {
            accuracy: position.accuracy,
            heading: position.heading,
            compassHeading: position.compassHeading,
            speed: position.speed,
            timestamp: position.timestamp,
          },
        };

        debug("Device position update:", {
          lat: position.lat?.toFixed(6),
          lng: position.lng?.toFixed(6),
          accuracy: position.accuracy,
          speed: position.speed,
          heading: position.heading?.toFixed(1),
          headingUnreliable,
        });

        this.notifySubscribers(trackingData);

        // Resolve on first successful position if starting
        if (!this._isActive) {
          this._isActive = true;
          resolve();
        }
      };

      const handleError = (error: GeolocationPositionError) => {
        debug.error("GPS error:", error);

        // If we haven't started yet, reject the promise
        if (!this._isActive) {
          reject(
            new Error(`GPS error: ${error.message || "Position unavailable"}`),
          );
          return;
        }

        // If already running, debounce the error to avoid flashing red during DevTools position changes
        // Chrome DevTools throws spurious errors when switching between positions
        // Wait 300ms - if we get a valid position in that time, ignore the error
        if (this.errorDebounceTimer) {
          clearTimeout(this.errorDebounceTimer);
        }

        this.errorDebounceTimer = setTimeout(() => {
          this.errorDebounceTimer = null;
          // Still no valid position after 300ms - treat as real error
          const errorData: TrackingData = {
            position: null,
            isActive: false,
            source: "device",
          };
          this.notifySubscribers(errorData);
        }, 300);
      };

      this.watchPositionId = navigator.geolocation.watchPosition(
        handlePosition,
        handleError,
        options,
      );

      debug("Started watching device position", {
        watchId: this.watchPositionId,
      });
    });
  }

  stop(): void {
    if (!this._isActive) return;

    if (this.watchPositionId !== null) {
      navigator.geolocation.clearWatch(this.watchPositionId);
      this.watchPositionId = null;
      debug("Stopped watching device position");
    }

    // Clear any pending error debounce timer
    if (this.errorDebounceTimer) {
      clearTimeout(this.errorDebounceTimer);
      this.errorDebounceTimer = null;
    }

    this.stopCompass();
    this.stopGpsHeadingSmoothing();

    this._isActive = false;
    this.hasReceivedPosition = false;

    const stoppedData: TrackingData = {
      position: null,
      isActive: false,
      source: "device",
    };
    this.notifySubscribers(stoppedData);
  }

  private startCompass(): void {
    if (this.compassHeading) return;

    // Start compass with interpolation timer at configured rate
    const maxUpdateRate =
      settingsService.getSettings().user.maxUpdateRate || 30;
    const intervalMs = 1000 / maxUpdateRate;

    let compassUpdateCount = 0;
    let lastCompassSecond = Date.now();

    this.compassHeading = new CompassHeading({
      smoothingFactor: 0.3, // Faster interpolation for 2Hz raw updates
      onUpdate: (heading: number) => {
        // This SHOULD be called at 60fps from requestAnimationFrame
        compassUpdateCount++;
        const now = Date.now();

        // Log the ACTUAL rate this is being called
        if (now - lastCompassSecond >= 1000) {
          debug(
            "COMPASS onUpdate RATE: %s/sec (should be 60)",
            compassUpdateCount,
          );
          compassUpdateCount = 0;
          lastCompassSecond = now;
        }

        // Store the latest smoothed value
        this.currentCompassHeading = heading;
      },
    });

    // Set up a separate timer to notify at the configured rate
    let updateCount = 0;
    let lastSecond = Date.now();

    this.currentUpdateRate = maxUpdateRate;
    this.compassIntervalId = window.setInterval(() => {
      if (this._isActive && this.currentCompassHeading !== null) {
        const now = Date.now();
        updateCount++;

        // Check if update rate changed - restart if so
        const currentMaxRate =
          settingsService.getSettings().user.maxUpdateRate || 30;
        if (currentMaxRate !== this.currentUpdateRate) {
          debug(
            "Update rate changed from %s to %s - restarting compass",
            this.currentUpdateRate,
            currentMaxRate,
          );
          this.stopCompass();
          this.startCompass();
          return;
        }

        // Only emit compass updates if NOT in GPS heading mode
        // In GPS heading mode, the GPS smoothing interval handles all heading updates
        const mapPrefs = settingsService.getMapPreferences();
        if (!mapPrefs.useGpsHeading) {
          // Create compass-only update with interpolated heading
          const compassOnlyData: TrackingData = {
            position: {
              lat: undefined,
              lng: undefined,
              compassHeading: this.currentCompassHeading,
              timestamp: now,
            },
            isActive: true,
            source: "device",
            metadata: {
              compassHeading: this.currentCompassHeading,
              timestamp: now,
            },
          };

          // Notify at configured rate
          this.notifySubscribers(compassOnlyData);
        }

        // Log actual rate every second
        if (now - lastSecond >= 1000) {
          debug(
            "INTERVAL FIRING RATE: %s/sec (target: %s)",
            updateCount,
            maxUpdateRate,
          );
          updateCount = 0;
          lastSecond = now;
        }
      }
    }, intervalMs);

    this.compassHeading.start();
    debug("Started compass heading tracking at %sfps", maxUpdateRate);
  }

  private stopCompass(): void {
    if (this.compassIntervalId) {
      clearInterval(this.compassIntervalId);
      this.compassIntervalId = null;
    }

    if (this.compassHeading) {
      this.compassHeading.stop();
      this.compassHeading = null;
      this.currentCompassHeading = null;
      debug("Stopped compass heading tracking");
    }
  }

  getCurrentCompassHeading(): number | null {
    return this.currentCompassHeading;
  }

  // Calculate angular distance
  private angleDiff(a: number, b: number): number {
    let diff = a - b;
    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;
    return diff;
  }

  // Check if a set of headings are consistent (within threshold)
  private areHeadingsConsistent(
    headings: number[],
    threshold: number,
  ): boolean {
    if (headings.length < 2) return false;
    const first = headings[0];
    return headings.every(
      (h) => Math.abs(this.angleDiff(h, first)) <= threshold,
    );
  }

  // Update compass calibration using GPS as ground truth
  private updateCompassCalibration(
    gpsHeading: number,
    compassHeading: number,
  ): void {
    const now = Date.now();

    // Add new reading
    this.compassCalibrationReadings.push({
      gps: gpsHeading,
      compass: compassHeading,
      timestamp: now,
    });

    // Remove readings older than 5 seconds
    this.compassCalibrationReadings = this.compassCalibrationReadings.filter(
      (r) => now - r.timestamp <= 5000,
    );

    // Need at least 5 readings
    if (this.compassCalibrationReadings.length < 5) return;

    // Check if GPS headings are consistent (within 10°)
    const gpsHeadings = this.compassCalibrationReadings.map((r) => r.gps);
    const compassHeadings = this.compassCalibrationReadings.map(
      (r) => r.compass,
    );

    if (!this.areHeadingsConsistent(gpsHeadings, 10)) {
      // GPS not stable, can't calibrate
      return;
    }

    if (!this.areHeadingsConsistent(compassHeadings, 10)) {
      // Compass not stable, can't calibrate
      return;
    }

    // Both are consistent - calculate offset
    const avgGps =
      gpsHeadings.reduce((sum, h) => sum + h, 0) / gpsHeadings.length;
    const avgCompass =
      compassHeadings.reduce((sum, h) => sum + h, 0) / compassHeadings.length;
    const offset = this.angleDiff(avgGps, avgCompass);

    // Only calibrate if GPS and compass disagree by more than 5°
    if (Math.abs(offset) > 5) {
      this.compassOffset = offset;
      debug(
        "📐 Compass calibrated: offset=%s° (GPS avg=%s°, Compass avg=%s°)",
        offset.toFixed(1),
        avgGps.toFixed(1),
        avgCompass.toFixed(1),
      );

      // Clear readings after successful calibration
      this.compassCalibrationReadings = [];
    }
  }

  private startGpsHeadingSmoothing(): void {
    if (this.gpsHeadingIntervalId) return;

    this.smoothedGpsHeading = this.targetGpsHeading;
    this.lastGpsHeadingUpdate = Date.now();

    const intervalMs = this.getUpdateInterval();
    const targetFps = 1000 / intervalMs;
    this.currentUpdateRate =
      settingsService.getSettings().user.maxUpdateRate || 30;

    this.gpsHeadingIntervalId = window.setInterval(() => {
      if (this._isActive) {
        // Check if update rate changed - restart if so
        const currentMaxRate =
          settingsService.getSettings().user.maxUpdateRate || 30;
        if (currentMaxRate !== this.currentUpdateRate) {
          debug(
            "Update rate changed from %s to %s - restarting GPS smoothing",
            this.currentUpdateRate,
            currentMaxRate,
          );
          this.stopGpsHeadingSmoothing();
          this.startGpsHeadingSmoothing();
          return;
        }

        this.updateSmoothedGpsHeading();
      }
    }, intervalMs);

    debug("Started GPS heading smoothing at %sfps", targetFps);
  }

  private stopGpsHeadingSmoothing(): void {
    if (this.gpsHeadingIntervalId) {
      clearInterval(this.gpsHeadingIntervalId);
      this.gpsHeadingIntervalId = null;
      this.targetGpsHeading = null;
      this.smoothedGpsHeading = null;
      debug("Stopped GPS heading smoothing");

      // Send update with no heading to show dot instead of arrow
      const noHeadingUpdate: TrackingData = {
        position: {
          lat: undefined,
          lng: undefined,
          heading: undefined,
          compassHeading: undefined,
          headingUnreliable: true,
          timestamp: Date.now(),
          isComplete: false,
        },
        isActive: true,
        source: "device",
        metadata: {
          heading: undefined,
          timestamp: Date.now(),
        },
      };
      this.notifySubscribers(noHeadingUpdate);
    }
  }

  private updateSmoothedGpsHeading(): void {
    const now = Date.now();
    const deltaTime = (now - this.lastGpsHeadingUpdate) / 1000;
    this.lastGpsHeadingUpdate = now;

    // Determine target: use GPS target if set, otherwise use live compass heading
    let actualTarget = this.targetGpsHeading;
    let isCompassMode = false;
    if (actualTarget === null && this.currentCompassHeading !== null) {
      // In compass mode - use live compass heading
      // Only apply calibration offset if GPS heading mode is enabled
      const mapPrefs = settingsService.getMapPreferences();
      const offset = mapPrefs.useGpsHeading ? this.compassOffset : 0;
      actualTarget = (this.currentCompassHeading + offset + 360) % 360;
      isCompassMode = true;
    }

    const headingUnreliable = actualTarget === null;

    // Initialize or smooth towards target
    if (actualTarget !== null) {
      if (this.smoothedGpsHeading === null) {
        this.smoothedGpsHeading = actualTarget;
      } else {
        // Calculate shortest angular distance
        let angleDiff = actualTarget - this.smoothedGpsHeading;
        if (angleDiff > 180) angleDiff -= 360;
        if (angleDiff < -180) angleDiff += 360;

        // Linear interpolation at constant rotation speed
        // GPS mode: slower rotation (720°/2sec = 360°/sec for 0.5s half rotation)
        // Compass mode: faster rotation (360°/2sec = 180°/sec)
        const maxDegreesPerSecond = isCompassMode ? 180 : 360;
        const maxRotationThisFrame = maxDegreesPerSecond * deltaTime;

        // Move toward target at constant speed, or snap if very close
        if (Math.abs(angleDiff) <= maxRotationThisFrame) {
          // Close enough - snap to target
          this.smoothedGpsHeading = actualTarget;
        } else {
          // Move at constant speed toward target
          const direction = angleDiff > 0 ? 1 : -1;
          this.smoothedGpsHeading += direction * maxRotationThisFrame;
          this.smoothedGpsHeading = (this.smoothedGpsHeading + 360) % 360;
        }
      }
    }

    // Emit heading-only update at configured FPS (like compass does)
    this.emitGpsHeadingUpdate(headingUnreliable);
  }

  private emitGpsHeadingUpdate(unreliable: boolean): void {
    if (!this._isActive) return;

    const headingValue = this.smoothedGpsHeading ?? undefined;

    const headingUpdate: Position = {
      lat: undefined,
      lng: undefined,
      heading: headingValue,
      compassHeading: headingValue, // KEY: Set compassHeading same as heading for map rotation!
      headingUnreliable: unreliable,
      timestamp: Date.now(),
      isComplete: false,
    };

    const trackingData: TrackingData = {
      position: headingUpdate,
      isActive: true,
      source: "device",
      metadata: {
        heading: headingValue,
        compassHeading: headingValue,
        timestamp: headingUpdate.timestamp,
      },
    };

    this.notifySubscribers(trackingData);
  }

  destroy(): void {
    debug("Destroying device tracking provider");
    this.stop();
    super.destroy();
  }
}
